import React, { useMemo, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';

const DebounceSelect = ({ fetchOptions, debounceTimeout = 500, ...props }) => {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState([]);
    const fetchRef = useRef(0);
    const abortControllerRef = useRef(null);

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value) => {
            if (!value) {
                setOptions([]);
                return;
            }

            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setFetching(true);

            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new controller for this request
            const controller = new AbortController();
            abortControllerRef.current = controller;

            fetchOptions(value, controller.signal).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    return;
                }
                setOptions(newOptions);
                setFetching(false);
            }).catch((error) => {
                if (error.name === 'AbortError') {
                    // Ignore abort errors
                    return;
                }
                console.error('Fetch options error:', error);
                setFetching(false);
            });
        };
        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);

    const handleChange = (val, opt) => {
        if (props.onChange) {
            // Form.Item injects onChange. By default val is just {label, value} due to labelInValue.
            if (!val) {
                props.onChange(null);
                return;
            }
            // We merge the full `opt` so custom fields like `participant` survive in form values.
            props.onChange({ ...val, participant: opt?.participant });
        }
    };

    return (
        <Select
            labelInValue
            filterOption={false}
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            {...props}
            onChange={handleChange}
            options={options}
        />
    );
};

export default DebounceSelect;
